import React from "react";
import AccordionTwoColumnGrid from "@/components/molecules/AccordionTwoColumnGrid";
import { useTranslation } from "react-i18next";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Grid } from "@mantine/core";
import { Controller, Control } from "react-hook-form";
import { RegisterCompanyFormInput } from "@/components/pages/RegisterCompany/validation/registerCompanyValidationSchema";
import TextInput from "@/components/atoms/TextInput";

interface CompanyDetailsProps {
  control: Control<RegisterCompanyFormInput>;
  saveDraft: () => Promise<void>;
}

const CompanyDetails: React.FC<CompanyDetailsProps> = ({ control, saveDraft }) => {
  const { t } = useTranslation();

  const leftContent = (
    <Stack>
      <Text size="lg" display="technical" tt="uppercase" fw={500} ff="heading">
        {t("registerCompany:companyDetails.title")}
      </Text>
      <Grid>
        <Grid.Col span={1} pr={0} mr={0}>
          <SVGAsset name="circle-questionmark" />
        </Grid.Col>
        <Grid.Col span={10} pl={0} ml={0}>
          <Text size="sm" color="secondary">
            {t("registerCompany:companyDetails.reason")}
          </Text>
        </Grid.Col>
      </Grid>
    </Stack>
  );

  const rightContent = (
    <Grid>
      <Grid.Col span={12}>
        <Controller
          name="partialCompany.name"
          control={control}
          render={({ field: { onChange, value, onBlur }, fieldState }) => (
            <TextInput
              data-testid={"company-name"}
              label={t("registerCompany:companyDetails.companyName")}
              value={value ?? ""}
              required
              onBlur={async () => {
                onBlur();
                await saveDraft();
              }}
              onChange={onChange}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Controller
          name="partialCompany.vatNumber"
          control={control}
          render={({ field: { onChange, value, onBlur }, fieldState }) => (
            <TextInput
              data-testid={"vat-number"}
              label={t("registerCompany:companyDetails.vatNumber")}
              value={value ?? ""}
              required
              onChange={onChange}
              onBlur={async () => {
                onBlur();
                await saveDraft();
              }}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Controller
          name="partialCompany.leiNumber"
          control={control}
          render={({ field: { onChange, value, onBlur }, fieldState }) => (
            <TextInput
              data-testid={"lei-number"}
              label={t("registerCompany:companyDetails.leiNumber")}
              value={value ?? ""}
              onChange={onChange}
              onBlur={async () => {
                onBlur();
                await saveDraft();
              }}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Controller
          name="partialCompany.eoriNumber"
          control={control}
          render={({ field: { onChange, value, onBlur }, fieldState }) => (
            <TextInput
              data-testid={"eori-number"}
              label={t("registerCompany:companyDetails.eoriNumber")}
              value={value ?? ""}
              onChange={onChange}
              onBlur={async () => {
                onBlur();
                await saveDraft();
              }}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>
    </Grid>
  );

  return <AccordionTwoColumnGrid leftContent={leftContent} rightContent={rightContent} />;
};

export default CompanyDetails;
